import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCw, ShieldCheck, AlertTriangle, Globe2 } from 'lucide-react';
import { toast } from 'sonner';
import { PreviewLinkButton } from './PreviewLinkButton';
import {
  PendingApprovalsSection,
  BeginTransferDialog,
  OverrideDialog,
  ForceReleaseDecisionDialog,
} from './EstateVaultAdminParts';
import ContinuityReadinessPanel from './ContinuityReadinessPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const headers = (json = false) => {
  const t = localStorage.getItem('admin_token') || localStorage.getItem('auth_token');
  const h = t ? { Authorization: `Bearer ${t}` } : {};
  if (json) h['Content-Type'] = 'application/json';
  return h;
};

const api = async (path, method = 'GET', body) => {
  const opts = { method, headers: headers(!!body) };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BACKEND_URL}${path}`, opts);
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try { const j = await res.json(); detail = j.detail || detail; } catch (_) { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
};

const STATUS_COLORS = {
  active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50',
  paused: 'bg-amber-500/20 text-amber-300 border-amber-500/50',
  triggered: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
  released: 'bg-slate-500/20 text-slate-300 border-slate-500/50',
  revoked: 'bg-rose-500/20 text-rose-300 border-rose-500/50',
};

const EstateVaultAdminTab = () => {
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [vaults, setVaults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [pendingVerifs, setPendingVerifs] = useState([]);
  const [pendingFR, setPendingFR] = useState([]);
  const [tier1, setTier1] = useState([]);
  const [tier1Draft, setTier1Draft] = useState('');
  const [overrideAction, setOverrideAction] = useState('');
  const [overrideJustification, setOverrideJustification] = useState('');
  const [overrideDays, setOverrideDays] = useState('30');
  const [transferSuccessor, setTransferSuccessor] = useState(null);
  const [transferJustification, setTransferJustification] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [frDialog, setFrDialog] = useState(null);
  const [frJustification, setFrJustification] = useState('');
  const [frSubmitting, setFrSubmitting] = useState(false);
  const [pendingTab, setPendingTab] = useState('force-release');
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [vaultsView, setVaultsView] = useState(() => localStorage.getItem('ev_vaults_view') || 'table');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${encodeURIComponent(filter)}` : '';
      const data = await api(`/api/admin/estate-vaults${q}`);
      setVaults(data.rows || []);
      const verifs = await api('/api/admin/estate-vault/verifications/pending');
      setPendingVerifs(verifs.rows || []);
      const fr = await api('/api/admin/estate-vault/force-release/pending');
      setPendingFR(fr.rows || []);
      const t1 = await api('/api/admin/estate-vault/tier1-countries');
      setTier1(t1.countries || []);
      setTier1Draft((t1.countries || []).join(', '));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (pendingTab === 'force-release' && pendingFR.length === 0 && pendingVerifs.length > 0) {
      setPendingTab('verifications');
    } else if (pendingTab === 'verifications' && pendingVerifs.length === 0 && pendingFR.length > 0) {
      setPendingTab('force-release');
    }
  }, [pendingFR.length, pendingVerifs.length, pendingTab]);

  const openDetail = async (vault) => {
    setSelected(vault);
    setDetail(null);
    try { setDetail(await api(`/api/admin/estate-vaults/${vault.vault_id}`)); }
    catch (e) { toast.error(e.message); }
  };

  const handleApproveVerif = async (id) => {
    try { await api(`/api/admin/estate-vault/verifications/${id}/approve`, 'POST', {}); toast.success('Approval recorded.'); await reload(); }
    catch (e) { toast.error(e.message); }
  };
  const handleRejectVerif = async (id) => {
    const reason = window.prompt('Rejection reason (min 5 chars):');
    if (!reason || reason.length < 5) return;
    try { await api(`/api/admin/estate-vault/verifications/${id}/reject`, 'POST', { reason }); toast.success('Rejection recorded.'); await reload(); }
    catch (e) { toast.error(e.message); }
  };

  const saveTier1 = async () => {
    const list = tier1Draft.split(',').map(s => s.trim()).filter(Boolean);
    try {
      const res = await api('/api/admin/estate-vault/tier1-countries', 'PUT', { countries: list });
      setTier1(res.countries);
      toast.success('Tier-1 country list saved.');
    } catch (e) { toast.error(e.message); }
  };

  const runOverride = async () => {
    if (!selected || !overrideAction || overrideJustification.length < 8) {
      toast.error('Action + justification (8+ chars) required.');
      return;
    }
    setOverrideSubmitting(true);
    try {
      const body = { action: overrideAction, justification: overrideJustification };
      if (overrideAction === 'extend_checkin') body.extend_days = parseInt(overrideDays, 10);
      const res = await api(`/api/admin/estate-vaults/${selected.vault_id}/override`, 'POST', body);
      toast.success(res?.pending_approval
        ? (res.message || 'Force-release request submitted. A different admin must approve.')
        : 'Override applied.');
      setOverrideAction(''); setOverrideJustification(''); setOverrideDays('30');
      setOverrideDialogOpen(false);
      await reload();
      await openDetail(selected);
    } catch (e) { toast.error(e.message); }
    finally { setOverrideSubmitting(false); }
  };

  const handleBeginTransfer = (successorId, fullName) => {
    if (!selected) return;
    setTransferSuccessor({ id: successorId, fullName });
    setTransferJustification('');
  };

  const submitBeginTransfer = async () => {
    if (!selected || !transferSuccessor) return;
    if (transferJustification.trim().length < 8) { toast.error('Please write at least 8 characters of context.'); return; }
    setTransferSubmitting(true);
    try {
      await api(
        `/api/admin/estate-vaults/${selected.vault_id}/successors/${transferSuccessor.id}/begin-transfer`,
        'POST',
        { justification: transferJustification.trim() },
      );
      toast.success('Information transfer started. The Trusted Contact will receive their secure link shortly.');
      setTransferSuccessor(null); setTransferJustification('');
      await openDetail(selected);
    } catch (e) { toast.error(e.message); }
    finally { setTransferSubmitting(false); }
  };

  const handleApproveFR = (req) => { setFrDialog({ mode: 'approve', request: req }); setFrJustification(''); };
  const handleRejectFR = (req) => { setFrDialog({ mode: 'reject', request: req }); setFrJustification(''); };
  const submitFrDecision = async () => {
    if (!frDialog) return;
    if (frJustification.trim().length < 8) { toast.error('Please write at least 8 characters of justification.'); return; }
    setFrSubmitting(true);
    try {
      const path = frDialog.mode === 'approve' ? 'approve' : 'reject';
      await api(
        `/api/admin/estate-vault/force-release/${frDialog.request.request_id}/${path}`,
        'POST',
        { justification: frJustification.trim() },
      );
      toast.success(frDialog.mode === 'approve'
        ? 'Force-release approved. The Continuity System has been released to its Trusted Contacts.'
        : 'Force-release rejected.');
      setFrDialog(null); setFrJustification('');
      await reload();
      if (selected) await openDetail(selected);
    } catch (e) { toast.error(e.message); }
    finally { setFrSubmitting(false); }
  };

  const runDms = async () => {
    try {
      const res = await api('/api/admin/estate-vault/process-dms', 'POST');
      toast.success(`DMS run: checked ${res.checked}, advanced ${res.advanced}, triggered ${res.triggered}`);
      await reload();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700 flex flex-wrap items-center gap-2" data-testid="ev-admin-toolbar">
        <h2 className="text-white text-lg font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-emerald-400" /> Continuity System</h2>
        <div className="ml-auto flex flex-wrap gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-gray-700 text-white border border-gray-600 rounded px-2 py-1 text-sm" data-testid="ev-admin-status-filter">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="triggered">Triggered</option>
            <option value="released">Released</option>
            <option value="revoked">Revoked</option>
          </select>
          <Button size="sm" variant="outline" onClick={reload} disabled={loading} data-testid="ev-admin-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={runDms} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ev-admin-run-dms">Run DMS scheduler</Button>
        </div>
      </div>

      {/* Tier-1 country editor */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700" data-testid="ev-admin-tier1">
        <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Globe2 className="w-4 h-4" /> Tier-1 Country List</h3>
        <p className="text-xs text-gray-400 mb-2">Comma-separated. Only countries on this list may activate Continuity System or be designated as a Trusted Contact.</p>
        <Textarea rows={2} value={tier1Draft} onChange={(e) => setTier1Draft(e.target.value)} className="bg-gray-900 text-white border-gray-600" data-testid="ev-admin-tier1-input" />
        <div className="mt-2 flex items-center gap-2">
          <Button size="sm" onClick={saveTier1} className="bg-blue-600 hover:bg-blue-700" data-testid="ev-admin-tier1-save">Save list</Button>
          <span className="text-xs text-gray-400">Current: {tier1.length} countries</span>
        </div>
      </div>

      {/* Phase C4 — aggregate Continuity readiness (counts only) */}
      <ContinuityReadinessPanel />

      <PendingApprovalsSection
        pendingFR={pendingFR}
        pendingVerifs={pendingVerifs}
        pendingTab={pendingTab}
        setPendingTab={setPendingTab}
        onApproveFR={handleApproveFR}
        onRejectFR={handleRejectFR}
        onApproveVerif={handleApproveVerif}
        onRejectVerif={handleRejectVerif}
      />

      {/* Vaults table / card view */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-white font-semibold">Vaults ({vaults.length})</h3>
          <div className="inline-flex rounded-md border border-gray-600 overflow-hidden text-xs" data-testid="ev-view-toggle">
            <button
              type="button"
              onClick={() => { setVaultsView('table'); localStorage.setItem('ev_vaults_view', 'table'); }}
              className={`px-3 py-1 ${vaultsView === 'table' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              data-testid="ev-view-table"
            >Table</button>
            <button
              type="button"
              onClick={() => { setVaultsView('cards'); localStorage.setItem('ev_vaults_view', 'cards'); }}
              className={`px-3 py-1 ${vaultsView === 'cards' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-gray-300 hover:bg-gray-700'}`}
              data-testid="ev-view-cards"
            >Cards</button>
          </div>
        </div>
        {vaults.length === 0 ? (
          <p className="text-sm text-gray-400">No vaults match the filter.</p>
        ) : vaultsView === 'table' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-gray-200">
              <thead className="text-xs uppercase text-gray-400 border-b border-gray-700">
                <tr>
                  <th className="py-2 pr-2">Vault ID</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Country</th>
                  <th className="py-2 pr-2">Activated</th>
                  <th className="py-2 pr-2">Next check-in</th>
                  <th className="py-2 pr-2">Successors</th>
                  <th className="py-2 pr-2">Quorum</th>
                  <th className="py-2 pr-2">DMS</th>
                  <th className="py-2 pr-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vaults.map((v) => (
                  <tr key={v.vault_id} className="border-b border-gray-700 hover:bg-gray-700/40" data-testid={`ev-row-${v.vault_id}`}>
                    <td className="py-2 pr-2 font-mono text-xs">{v.vault_id}</td>
                    <td className="py-2 pr-2"><Badge className={`border ${STATUS_COLORS[v.status] || ''}`}>{v.status}</Badge></td>
                    <td className="py-2 pr-2">{v.owner_country_at_activation || '—'}</td>
                    <td className="py-2 pr-2 text-xs">{v.activated_at?.slice(0, 10) || '—'}</td>
                    <td className="py-2 pr-2 text-xs">{v.next_checkin_due_at?.slice(0, 10) || '—'}</td>
                    <td className="py-2 pr-2">{v.successor_count}</td>
                    <td className="py-2 pr-2" data-testid={`ev-quorum-${v.vault_id}`}>
                      {(v.status === 'triggered' || v.status === 'released')
                        ? `${v.quorum_verified_count || 0}/${v.quorum_required || 2}`
                        : '—'}
                    </td>
                    <td className="py-2 pr-2">{v.dms_stage || 0}/3</td>
                    <td className="py-2 pr-2">
                      <Button size="sm" variant="outline" onClick={() => openDetail(v)} data-testid={`ev-detail-${v.vault_id}`}>View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ev-cards-grid">
            {vaults.map((v) => (
              <div
                key={v.vault_id}
                className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 hover:border-emerald-600 transition-colors"
                data-testid={`ev-card-${v.vault_id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <Badge className={`border ${STATUS_COLORS[v.status] || ''}`}>{v.status}</Badge>
                  <span className="font-mono text-[10px] text-gray-500">{v.vault_id}</span>
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs text-gray-300 mb-3">
                  <div><span className="text-gray-500">Country</span><br /><span className="text-gray-200">{v.owner_country_at_activation || '—'}</span></div>
                  <div><span className="text-gray-500">Successors</span><br /><span className="text-gray-200">{v.successor_count}</span></div>
                  <div><span className="text-gray-500">Activated</span><br /><span className="text-gray-200">{v.activated_at?.slice(0, 10) || '—'}</span></div>
                  <div><span className="text-gray-500">Next check-in</span><br /><span className="text-gray-200">{v.next_checkin_due_at?.slice(0, 10) || '—'}</span></div>
                  <div className="col-span-2"><span className="text-gray-500">DMS stage</span>{' '}<span className={`text-gray-200 ${(v.dms_stage || 0) > 0 ? 'text-orange-300 font-semibold' : ''}`}>{v.dms_stage || 0}/3</span></div>
                  {(v.status === 'triggered' || v.status === 'released') && (
                    <div className="col-span-2" data-testid={`ev-quorum-card-${v.vault_id}`}>
                      <span className="text-gray-500">Quorum</span>{' '}
                      <span className="text-emerald-300 font-semibold">
                        {v.quorum_verified_count || 0}/{v.quorum_required || 2} confirmed
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  size="sm" variant="outline" onClick={() => openDetail(v)}
                  className="w-full" data-testid={`ev-detail-${v.vault_id}`}
                >View details</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="bg-gray-800 rounded-xl p-4 border border-emerald-600" data-testid="ev-admin-detail">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-semibold">Vault {selected.vault_id}</h3>
            <Button size="sm" variant="outline" onClick={() => setSelected(null)}>Close</Button>
          </div>
          {!detail ? <p className="text-sm text-gray-400">Loading…</p> : (
            <div className="space-y-3 text-sm text-gray-200">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>Status: <Badge className={`border ${STATUS_COLORS[detail.vault.status] || ''}`}>{detail.vault.status}</Badge></div>
                <div>DMS stage: {detail.vault.dms_stage || 0}/3</div>
                <div>Activated: {detail.vault.activated_at}</div>
                <div>Last check-in: {detail.vault.last_checkin_response_at}</div>
              </div>

              <div>
                <strong>Successors:</strong>
                {detail.successors.length === 0 ? <span className="text-gray-400 text-xs"> none</span> : (
                  <ul className="pl-3 text-xs space-y-1 mt-1">
                    {detail.successors.map((s) => (
                      <li key={s.successor_id} className="flex items-center justify-between gap-2 border border-gray-700 rounded px-2 py-1">
                        <span>{s.full_name} &middot; {s.relationship} &middot; {s.country} &middot; {s.status}</span>
                        <div className="flex items-center gap-2">
                          <PreviewLinkButton
                            endpoint={`/api/admin/estate-vault/${selected.vault_id}/successors/${s.successor_id}/preview-link`}
                            label="Preview as Trusted Contact"
                            testIdSuffix={`succ-${s.successor_id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleBeginTransfer(s.successor_id, s.full_name)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7"
                            data-testid={`ev-begin-transfer-${s.successor_id}`}
                            title="Emails this person a secure, one-time link to upload their verification documents. They have 24 hours. Two of our staff must approve before any information is shared."
                          >
                            Begin information transfer
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <strong>Check-ins:</strong>
                {detail.checkins.length === 0 ? <span className="text-gray-400 text-xs"> none</span> : (
                  <ul className="list-disc pl-5 text-xs">
                    {detail.checkins.slice(0, 6).map((c) => (
                      <li key={c.checkin_id}>{c.sent_at?.slice(0, 16)} · stage {c.stage} · resp: {c.response || 'none'}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <strong>Audit log (last 20):</strong>
                <ul className="list-disc pl-5 text-xs max-h-40 overflow-y-auto">
                  {detail.audit_log.slice(0, 20).map((a) => (
                    <li key={a._id || a.created_at}>{a.created_at?.slice(0, 16)} · {a.actor_type}/{a.action}</li>
                  ))}
                </ul>
              </div>

              <div className="border-t border-gray-700 pt-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <h4 className="text-white text-sm font-semibold flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4 text-amber-400" /> Manual override
                    </h4>
                    <p className="text-xs text-gray-400 mt-1 max-w-md">
                      Owner-only intervention &mdash; pause, resume, extend a check-in, force release, or revoke. Justification is mandatory and audit-logged.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => { setOverrideAction(''); setOverrideJustification(''); setOverrideDays('30'); setOverrideDialogOpen(true); }}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="ev-override-open"
                  >
                    <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Open override panel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <BeginTransferDialog
        transferSuccessor={transferSuccessor}
        setTransferSuccessor={setTransferSuccessor}
        transferJustification={transferJustification}
        setTransferJustification={setTransferJustification}
        transferSubmitting={transferSubmitting}
        onSubmit={submitBeginTransfer}
      />

      <OverrideDialog
        overrideDialogOpen={overrideDialogOpen}
        setOverrideDialogOpen={setOverrideDialogOpen}
        overrideAction={overrideAction}
        setOverrideAction={setOverrideAction}
        overrideJustification={overrideJustification}
        setOverrideJustification={setOverrideJustification}
        overrideDays={overrideDays}
        setOverrideDays={setOverrideDays}
        overrideSubmitting={overrideSubmitting}
        onRun={runOverride}
      />

      <ForceReleaseDecisionDialog
        frDialog={frDialog}
        setFrDialog={setFrDialog}
        frJustification={frJustification}
        setFrJustification={setFrJustification}
        frSubmitting={frSubmitting}
        onSubmit={submitFrDecision}
      />
    </div>
  );
};

export default EstateVaultAdminTab;
